/**
 * Medición del consumo de datos del dispositivo.
 *
 * ──────────────────────────────────────────────────────────────────────────
 * CÓMO SE MIDE
 * ──────────────────────────────────────────────────────────────────────────
 *
 * Con `PerformanceResourceTiming.transferSize`, que son los bytes REALES que
 * cruzaron la red por cada petición, encabezados incluidos. No requiere
 * permisos, funciona igual en Android y en iOS, y no depende del sistema
 * operativo.
 *
 * Tres propiedades que lo hacen el instrumento correcto aquí:
 *
 *   · Lo servido desde caché mide CERO, que es exactamente lo que se quiere:
 *     no gastó datos del plan.
 *   · Incluye los encabezados. Una llamada cuyo cuerpo son 159 bytes mide 459
 *     de cable; cobrar sólo el cuerpo subestimaría casi el triple.
 *   · Al ser el API del MISMO ORIGEN que la aplicación, no hace falta
 *     `Timing-Allow-Origin`: en origen cruzado `transferSize` devuelve 0 y
 *     toda la sincronización se contaría como gratis.
 *
 * LÍMITE CONOCIDO: lo que el service worker descargue por su cuenta, fuera de
 * una pestaña viva, no aparece en esta medición. Hoy la PWA no sincroniza en
 * segundo plano, así que no hay hueco; si algún día lo hace, hay que medirlo
 * también desde el worker.
 *
 * ──────────────────────────────────────────────────────────────────────────
 * CÓMO SE CLASIFICA
 * ──────────────────────────────────────────────────────────────────────────
 *
 * La clasificación ocurre AL CONSUMIR, no al enviar. El encuestador gasta sus
 * datos en campo y sincroniza al llegar a un WiFi: si se clasificara en el
 * momento del envío, todo el gasto del día quedaría marcado como WiFi y el
 * reembolso sería cero.
 */

import { db } from '../db/db';
import { api } from '../api/client';
import type { ConnectionKind, ConnectionSource } from '@capi/shared';

export interface UsageSample {
  bytes: number;
  connection: ConnectionKind;
  source: ConnectionSource;
  asn: number | null;
  startedAt: string;
  endedAt: string;
}

export interface ConnectionContext {
  connection: ConnectionKind;
  asn: number | null;
  ispName: string | null;
  known: boolean;
  /** La petición salió de una red local: no hay proveedor que resolver. */
  privateNetwork: boolean;
}

/** Cada cuánto se cierra un tramo y se guarda. */
const FLUSH_MS = 30_000;
/** Cada cuánto se vuelve a preguntar al servidor por la red actual. */
const CONTEXT_TTL_MS = 5 * 60 * 1000;

let bytesAcumulados = 0;
let tramoDesde = new Date().toISOString();
let contexto: ConnectionContext = {
  connection: 'desconocida',
  asn: null,
  ispName: null,
  known: false,
  privateNetwork: false,
};
let contextoActualizado = 0;
let observer: PerformanceObserver | null = null;
let timer: number | null = null;
const listeners = new Set<() => void>();

/* -------------------------------------------------------------------------- */
/*                            Clasificación                                   */
/* -------------------------------------------------------------------------- */

/**
 * Clasificación vigente, decidida ÚNICAMENTE por el servidor.
 *
 * No hay ajuste manual a propósito. Esta medición alimenta un reembolso, y un
 * interruptor en la pantalla del encuestador le dejaría decidir sobre su
 * propio pago. Las correcciones —un ASN que falta en la tabla, un teléfono
 * compartiendo conexión— se resuelven desde el panel de coordinación, que es
 * donde la decisión no es juez y parte.
 *
 * El costo de quitarlo: el ASN no distingue a un encuestador que comparte la
 * conexión de su teléfono a otro aparato. Ese tráfico se clasificará por la
 * red del que navega, no por la del que paga.
 */
export function effectiveConnection(): { connection: ConnectionKind; source: ConnectionSource } {
  if (contexto.connection !== 'desconocida') return { connection: contexto.connection, source: 'asn' };
  return { connection: 'desconocida', source: 'desconocido' };
}

export function getContext(): ConnectionContext {
  return contexto;
}

export function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function notify(): void {
  for (const fn of listeners) fn();
}

/* -------------------------------------------------------------------------- */
/*                               Medición                                     */
/* -------------------------------------------------------------------------- */

function sumar(entries: readonly PerformanceEntry[]): void {
  for (const entry of entries) {
    const r = entry as PerformanceResourceTiming;
    // `transferSize` indefinido = el navegador no lo expone para ese recurso.
    // Sumar 0 es correcto: preferimos subestimar antes que inventar.
    if (typeof r.transferSize === 'number' && r.transferSize > 0) {
      bytesAcumulados += r.transferSize;
    }
  }
}

/** Cierra el tramo abierto y lo guarda para enviarlo después. */
export function flushTramo(): void {
  if (bytesAcumulados <= 0) {
    tramoDesde = new Date().toISOString();
    return;
  }
  const { connection, source } = effectiveConnection();
  const sample: UsageSample = {
    bytes: Math.round(bytesAcumulados),
    connection,
    source,
    asn: contexto.asn,
    startedAt: tramoDesde,
    endedAt: new Date().toISOString(),
  };
  bytesAcumulados = 0;
  tramoDesde = sample.endedAt;

  // Se guarda en el dispositivo antes de intentar enviarlo: si se cierra la
  // aplicación o no hay señal, el tramo no se pierde.
  void db.usage.add(sample).then(notify).catch(() => undefined);
}

/**
 * Pregunta al servidor por qué red está entrando la petición.
 *
 * Es la única señal automática que existe: el navegador no puede decirlo.
 */
export async function refreshConnection(force = true): Promise<void> {
  return refreshContext(force);
}

async function refreshContext(force = false): Promise<void> {
  if (!navigator.onLine) return;
  if (!force && Date.now() - contextoActualizado < CONTEXT_TTL_MS) return;
  try {
    const result = await api.connectionContext();
    // Cerrar el tramo ANTES de cambiar de clasificación: los bytes ya
    // consumidos pertenecen a la red anterior.
    if (result.connection !== contexto.connection) flushTramo();
    contexto = result;
    contextoActualizado = Date.now();
    notify();
  } catch {
    /* sin red o sin permiso: la conexión se queda como estaba */
  }
}

/** Envía los tramos pendientes. Se llama desde la sincronización. */
export async function sendPending(deviceId: string | null): Promise<number> {
  flushTramo();
  const pendientes = await db.usage.toArray();
  if (pendientes.length === 0) return 0;

  // En lotes, porque el API acepta 200 por envío.
  const lote = pendientes.slice(0, 200);
  await api.reportDataUsage({
    deviceId,
    samples: lote.map(({ id: _id, ...rest }) => rest),
  });
  await db.usage.bulkDelete(lote.map((s) => s.id!).filter((id) => id !== undefined));
  notify();
  return lote.length;
}

/** Total pendiente de enviar, para sumarlo a lo que ya está en el servidor. */
export async function pendingTotals(): Promise<{ movil: number; wifi: number }> {
  const pendientes = await db.usage.toArray();
  return pendientes.reduce(
    (acc, s) => ({
      movil: acc.movil + (s.connection === 'movil' ? s.bytes : 0),
      wifi: acc.wifi + (s.connection === 'wifi' ? s.bytes : 0),
    }),
    { movil: 0, wifi: 0 },
  );
}

/* -------------------------------------------------------------------------- */
/*                            Ciclo de vida                                   */
/* -------------------------------------------------------------------------- */

export function startDataUsage(): void {
  if (observer) return;

  // Limpieza de la versión anterior: hubo un interruptor manual que guardaba
  // su elección aquí. El código ya no lo lee, pero los teléfonos que
  // alcanzaron esa versión conservan la clave; borrarla evita que alguien la
  // encuentre después y crea que todavía hace algo.
  try {
    localStorage.removeItem('capi.connectionOverride');
  } catch {
    /* almacenamiento bloqueado: no es crítico */
  }

  // Lo ya ocurrido antes de arrancar el observador también cuenta.
  try {
    sumar(performance.getEntriesByType('resource'));
    sumar(performance.getEntriesByType('navigation'));
  } catch {
    /* navegador sin Resource Timing: la medición queda en cero */
  }

  try {
    observer = new PerformanceObserver((list) => sumar(list.getEntries()));
    observer.observe({ type: 'resource', buffered: true });
  } catch {
    observer = null;
  }

  timer = window.setInterval(() => {
    flushTramo();
    void refreshContext();
  }, FLUSH_MS);

  void refreshContext(true);

  // Al recuperar red conviene reconfirmar: cambiar de datos a WiFi es
  // justamente el evento que hay que captar a tiempo.
  window.addEventListener('online', () => void refreshContext(true));

  // Al ocultar la pestaña se cierra el tramo: puede que no vuelva a abrirse.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushTramo();
  });
}

export function stopDataUsage(): void {
  observer?.disconnect();
  observer = null;
  if (timer !== null) window.clearInterval(timer);
  timer = null;
  flushTramo();
}
